import { useEffect, useMemo, useRef, useState } from 'react';
import { Map } from 'google-maps-react-markers';
import { useRouter } from 'next/router';
import {
  CourtSurfacesEnum,
  SportsEnum,
  VenueAccessTypesEnum,
  VenueNetsEnum,
  useGetVenuesByGeoLazyQuery,
} from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useInView } from 'hooks/useInView';
import { useModal } from 'hooks/useModal';
import { GoogleMapProps } from 'components/GoogleMap/props';
import CourtFilters from './CourtFilters';
import CourtList from './CourtList';
import CourtMapMarker from './CourtMapMarker';
import { CourtType, ExploreCourtsProps, UseCourtReturn } from './props';
import {
  DEFAULT_COURTS_MAX,
  DEFAULT_COURT_DISTANCE_IMPERIAL,
  VENUE_ACCESS_OPTIONS,
  VENUE_NETS_OPTIONS,
  VENUE_SURFACE_OPTIONS,
  getCourtLimit,
} from './utils';

const useCourt = ({ resultsLimit, initialSport }: ExploreCourtsProps = {}): UseCourtReturn => {
  const { position, centerLatitude, centerLongitude, isExactCenter } = useGeoLocation();
  const router = useRouter();

  const getInitialSport = () => {
    // If initialSport is provided from server-side data, use it
    if (initialSport) {
      return initialSport;
    }

    // Check for sport query parameter
    const sportParam = router.query.sport as string;
    if (sportParam) {
      const sport = Object.values(SportsEnum).find(
        (val) => val.toLowerCase() === sportParam.toLowerCase(),
      );
      return sport as SportsEnum;
    }
    return SportsEnum.Pickleball;
  };

  const [selectedSport, setSelectedSport] = useState<SportsEnum>(getInitialSport());

  // Sync sport when the nav search bar shallow-pushes a new ?sport= param
  useEffect(() => {
    const sportParam = router.query.sport as string;
    if (!sportParam) return;
    const matched = Object.values(SportsEnum).find(
      (val) => val.toLowerCase() === sportParam.toLowerCase(),
    );
    if (matched) setSelectedSport(matched);
  }, [router.query.sport]);
  const [courtType, setCourtType] = useState<CourtType[]>([]);
  const [surface, setSurface] = useState<CourtSurfacesEnum[]>([]);
  const [nets, setNets] = useState<VenueNetsEnum[]>([]);
  const [accessType, setAccessType] = useState<VenueAccessTypesEnum[]>([]);
  /**
   * @note over 90% of data says permnanet lines which can't be true. It must count tennis courts too which is bad data.
   */
  // const [lines, setLines] = useState('');
  const [courtsMaxNumber, setCourtsMaxNumber] = useState(DEFAULT_COURTS_MAX);
  const [courtsMinNumber, setCourtsMinNumber] = useState(1);
  const [showOnlyDedicatedCourts, setShowOnlyDedicatedCourts] = useState(false);
  const [getVenuesByGeoLazyQuery, { data, loading, called }] = useGetVenuesByGeoLazyQuery();
  const [listHoveredVenueId, setListHoveredVenueId] = useState<string | null>(null);
  const [activeVenueId, setActiveVenueId] = useState<string | null>(null);
  const [distance, setDistance] = useState(DEFAULT_COURT_DISTANCE_IMPERIAL);

  // UI state management
  const { closeModal, openModal, isOpen: isModalOpen } = useModal();
  const discoverContainerRef = useRef<HTMLDivElement | null>(null);
  const [isOpenMap, setIsOpenMap] = useState<boolean>(false);
  const isDiscoverSectionInView = useInView(discoverContainerRef);

  const fetchCourts = ({
    longitude,
    latitude,
    distance,
  }: {
    longitude: number;
    latitude: number;
    distance: number;
  }) => {
    getVenuesByGeoLazyQuery({
      fetchPolicy: 'network-only',
      variables: {
        limit: !!resultsLimit ? resultsLimit : null,
        distance,
        from: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
      },
    });
  };

  /**
   * Distance is applied by the server (`_stDWithin` in `getVenuesByGeo`) and there is no
   * client-side distance narrowing, so changing it has to refetch — without this the
   * header's distance dropdown was inert and only the filters modal, which called
   * `fetchCourts` itself, ever moved the radius.
   */
  useEffect(() => {
    if (position) {
      fetchCourts({
        longitude: position.longitude,
        latitude: position.latitude,
        distance: milesToMeters(distance.id),
      });
    }
  }, [position, resultsLimit, distance]);

  useEffect(() => {
    if (!called && position && !data?.venues.length) {
      getVenuesByGeoLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          distance: milesToMeters(DEFAULT_COURT_DISTANCE_IMPERIAL.id),
          from: {
            type: 'Point',
            coordinates: [centerLongitude, centerLatitude],
          },
        },
      });
    }
  }, [data, position, called]);

  const decoratedVenues = useMemo(() => {
    if (!data?.venues || data.venues.length === 0) {
      return [];
    }

    return data.venues.map((venue) => {
      return {
        ...venue,
        distance: calculateHaversineDistance({
          coord1: {
            latitude: centerLatitude || 0,
            longitude: centerLongitude || 0,
          },
          coord2: {
            latitude: venue.geometry.coordinates[1],
            longitude: venue.geometry.coordinates[0],
          },
          unit: 'miles',
        }),
      };
    });
  }, [data?.venues, centerLatitude, centerLongitude]);

  const venues = useMemo(() => {
    const limit = getCourtLimit(decoratedVenues);
    const maxCourts = Math.min(limit, courtsMaxNumber);
    const minCourts = Math.min(maxCourts, courtsMinNumber);

    let venues = decoratedVenues.filter((court) => {
      const totalCourts = (court.indoorCourtCount || 0) + (court.outdoorCourtCount || 0);
      return totalCourts >= minCourts && totalCourts <= maxCourts;
    });

    // Filter by sport
    venues = venues.filter((venue) => {
      switch (selectedSport) {
        case SportsEnum.Pickleball:
          return venue.hasPickleball;
        case SportsEnum.Tennis:
          return venue.hasTennis;
        case SportsEnum.Padel:
          return venue.hasPadel || false;
        default:
          return true;
      }
    });

    // Each chip stands for a group of underlying enum values (Membership also covers
    // Private), so selections are expanded through `includedValues` — matching only the
    // chip's own id previously collapsed every selection to Free, which meant picking
    // "Membership" or "One time" returned free courts.
    if (accessType.length > 0) {
      const accessIncluded = accessType.flatMap(
        (access) =>
          VENUE_ACCESS_OPTIONS.find((option) => option.id === access)?.includedValues ?? [],
      );

      venues = venues.filter(
        (venue) => !!venue.accessType && accessIncluded.includes(venue.accessType),
      );
    }

    if (showOnlyDedicatedCourts) {
      venues = venues.filter((venue) => {
        return (
          venue.pickleballNets === VenueNetsEnum.Permanent ||
          venue.pickleballNets === VenueNetsEnum.Tennis
        );
      });
    } else if (nets.length > 0) {
      const netsIncluded = nets.flatMap(
        (net) => VENUE_NETS_OPTIONS.find((option) => option.id === net)?.includedValues ?? [],
      );

      venues = venues.filter(
        (venue) => !!venue.pickleballNets && netsIncluded.includes(venue.pickleballNets),
      );
    }

    if (surface.length > 0) {
      let surfacesIncluded: CourtSurfacesEnum[] = [];

      surface.forEach((surface) => {
        const matchingSurface = VENUE_SURFACE_OPTIONS.find((option) => option.id === surface);
        if (!matchingSurface) {
          return;
        }
        surfacesIncluded = [...surfacesIncluded, ...matchingSurface?.includedValues];
      });

      venues = venues.filter((venue) => {
        let hasSurface = false;
        venue.courtSurfaces.forEach((courtSurface) => {
          if (surfacesIncluded.includes(courtSurface.courtSurface)) {
            hasSurface = true;
          }
        });
        return hasSurface;
      });
    }

    // Selections are OR'd, matching the court-type chips on socials/open play/clinics.
    // Selecting both used to skip the filter entirely, which let venues with no courts
    // of either kind through.
    if (courtType.length > 0) {
      venues = venues.filter((venue) =>
        courtType.some((type) =>
          type === CourtType.Indoor ? venue.indoorCourtCount > 0 : venue.outdoorCourtCount > 0,
        ),
      );
    }

    return venues.sort((a, b) => a.distance - b.distance);
  }, [
    decoratedVenues,
    courtsMinNumber,
    courtsMaxNumber,
    showOnlyDedicatedCourts,
    surface,
    courtType,
    nets,
    accessType,
    selectedSport,
  ]);

  const activeVenue = venues.find((venue) => venue.id === activeVenueId);

  const courtLimit = useMemo(() => {
    return getCourtLimit(decoratedVenues);
  }, [decoratedVenues]);

  const maxCourts = Math.min(courtLimit, courtsMaxNumber);
  const minCourts = Math.min(maxCourts, courtsMinNumber);

  const handleGoogleApiLoaded = (map: Map) => {
    map.setOptions({
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      },
    });
  };

  const courtMapProps: GoogleMapProps = {
    syncDragToURL: true,
    center: {
      lat: centerLatitude,
      lng: centerLongitude,
    },
    zoom: 12,
    centerIcon: (
      <div className="h-8 w-8 rounded-full border-[12px] border-color-border-brand bg-white" />
    ),
    markers: venues.map((venue) => ({
      zIndex: activeVenue?.id === venue.id ? 2 : 1,
      lat: venue.geometry.coordinates[1],
      lng: venue.geometry.coordinates[0],
      markerId: venue.id,
      children: (
        <CourtMapMarker
          venue={venue}
          activeVenue={activeVenue}
          setActiveVenueId={setActiveVenueId}
          listHoveredVenueId={listHoveredVenueId}
          setListHoveredVenueId={setListHoveredVenueId}
          selectedSport={selectedSport}
        />
      ),
    })),
    isExactCenter,
    onGoogleApiLoaded: handleGoogleApiLoaded,
  };

  const clearCourtFilters = () => {
    setNets([]);
    setAccessType([]);
    setCourtType([]);
    setSurface([]);
    setShowOnlyDedicatedCourts(false);
    setCourtsMaxNumber(DEFAULT_COURTS_MAX);
    setCourtsMinNumber(1);
    // Resetting the radius is enough — the `distance` effect above issues the refetch.
    setDistance(DEFAULT_COURT_DISTANCE_IMPERIAL);
  };

  const onAddressChange = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    fetchCourts({
      longitude,
      latitude,
      distance: milesToMeters(distance.id),
    });
  };

  const handleToggleMap = () => {
    setIsOpenMap((curr) => !curr);
  };

  // Handle scroll behavior when map is toggled
  useEffect(() => {
    if (discoverContainerRef.current && isOpenMap)
      discoverContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
  }, [isOpenMap]);

  return {
    CourtList: (
      <CourtList
        loading={loading}
        venues={venues}
        setListHoveredVenueId={setListHoveredVenueId}
        listHoveredVenueId={listHoveredVenueId}
        selectedSport={selectedSport}
      />
    ),
    CourtFilters: (
      <CourtFilters
        selectedSport={selectedSport}
        setAccessType={setAccessType}
        accessType={accessType}
        courtType={courtType}
        setCourtType={setCourtType}
        setNets={setNets}
        nets={nets}
        setSurface={setSurface}
        surface={surface}
        courtsMinNumber={minCourts}
        setCourtsMinNumber={setCourtsMinNumber}
        showOnlyDedicatedCourts={showOnlyDedicatedCourts}
        setShowOnlyDedicatedCourts={setShowOnlyDedicatedCourts}
        courtsMaxNumber={maxCourts}
        setCourtsMaxNumber={(value) =>
          setCourtsMaxNumber(value >= courtLimit ? DEFAULT_COURTS_MAX : value)
        }
        courtLimit={courtLimit}
        distance={distance}
        setDistance={setDistance}
      />
    ),
    courtMapProps,
    clearCourtFilters,
    distance,
    setDistance,
    onAddressChange,
    courtsLength: venues.length,
    // Sports filtering
    selectedSport,
    setSelectedSport,
    // UI state and handlers
    isModalOpen,
    openModal,
    closeModal,
    isOpenMap,
    handleToggleMap,
    discoverContainerRef,
    isDiscoverSectionInView,
  };
};

export default useCourt;
